import { useState } from "react";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import TamaguiInput from "../components/shared/tamagui-input";

import Typography from "../components/shared/typography";

function DataDeletionRequestPage() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setError("");

    try {
      // TODO: Replace with actual API endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/data-deletion-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: email.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit deletion request");
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center" variant="cream">
          <Card.Header variant="cream">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <Typography variant="h3" className="text-primary mb-2">
              Request Submitted
            </Typography>
            <Typography variant="body1" className="text-gray-600">
              We've received your data deletion request. Our team will review
              and process it within 30 days as required by data protection
              regulations.
            </Typography>
          </Card.Header>
          <Card.Body>
            <Typography variant="body2" className="text-gray-500">
              You will receive a confirmation email shortly. If you have any
              questions, please contact our support team.
            </Typography>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="w-full max-w-md" variant="cream">
        <Card.Header variant="cream" className="text-center">
          <Typography variant="h3" className="text-primary mb-4">
            Request Data Deletion
          </Typography>
          <Typography variant="body1" className="text-gray-600 mb-2">
            We're sorry to see you go. To request deletion of your account and
            all associated data, please enter your email address below.
          </Typography>
          <Typography variant="body2" className="text-gray-500">
            This process will permanently remove your account, medical records,
            chat history, and all other data. This action cannot be undone.
          </Typography>
        </Card.Header>
        <Card.Body>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TamaguiInput
              id="email"
              label="Email Address"
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email address"
              fullWidth
              inputType="email"
            />

            {error && (
              <Typography variant="body2" className="text-red-600 text-center">
                {error}
              </Typography>
            )}

            <SubmitButton
              type="submit"
              isDisabled={!email.trim() || isSubmitting}
              isLoading={isSubmitting}
              className="w-full"
              color="brown"
            >
              Submit Deletion Request
            </SubmitButton>
          </form>
        </Card.Body>
        <Card.Footer variant="cream" className="text-center">
          <Typography variant="caption" className="text-gray-500">
            By submitting this request, you acknowledge that all your data will
            be permanently deleted within 30 days.
          </Typography>
        </Card.Footer>
      </Card>
    </div>
  );
}

export default DataDeletionRequestPage;
